import {
  ActionIcon,
  Checkbox,
  Group,
  Menu,
  ScrollArea,
  Text,
} from "@mantine/core";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import SvgIconRaw from "../SvgIconRaw";
import ExportCsvButton from "./ExportCsvButton";
import GlobalFilterInput from "./GlobalFilterInput";
import type {
  MeqDensity,
  MeqRowData,
  MeqTableInstance,
  MeqTableLocalization,
} from "./types";

/**
 * Composes every MeqTable toolbar affordance (spike's toolbar-composition
 * sketch, plans/ins-1606/learnings.md): title + custom-actions slot (left);
 * global filter search, show/hide-columns menu, density toggle, fullscreen
 * toggle, CSV export (right) — each individually disableable via its `has*`
 * prop. Consumes ONLY the owned `MeqTableInstance` facade, never the raw
 * TanStack table instance (architecture rule).
 *
 * Every aria-label/menu label defaults from i18next `table.*` and is
 * overridable via `localization` (INS-1606 i18n-localization task), same
 * pattern as MeqTable.tsx/GlobalFilterInput.tsx.
 */

const DENSITY_ORDER: MeqDensity[] = ["compact", "comfortable", "spacious"];

export interface ToolbarProps<TData extends MeqRowData> {
  title?: string;
  meqInstance: MeqTableInstance<TData>;
  renderToolbarActions?: (props: {
    table: MeqTableInstance<TData>;
  }) => ReactNode;
  renderToolbarActionsRight?: (props: {
    table: MeqTableInstance<TData>;
  }) => ReactNode;
  renderToolbarActionsRightEnd?: (props: {
    table: MeqTableInstance<TData>;
  }) => ReactNode;
  hasGlobalFilter?: boolean;
  hasColumnHiding?: boolean;
  hasDensityToggle?: boolean;
  hasFullscreenToggle?: boolean;
  hasBorderedControls?: boolean;
  hasExport?: boolean;
  isExportButtonIcon?: boolean;
  onExportDataToCsv?: () => void;
  exportFileName?: string;
  density: MeqDensity;
  onDensityChange: (density: MeqDensity) => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  localization?: Partial<MeqTableLocalization>;
}

const Toolbar = <TData extends MeqRowData>({
  title,
  meqInstance,
  renderToolbarActions,
  renderToolbarActionsRight,
  renderToolbarActionsRightEnd,
  hasGlobalFilter = true,
  hasColumnHiding = true,
  hasDensityToggle = false,
  hasFullscreenToggle = true,
  hasBorderedControls = false,
  hasExport = false,
  isExportButtonIcon = true,
  onExportDataToCsv,
  exportFileName,
  density,
  onDensityChange,
  isFullscreen,
  onToggleFullscreen,
  localization,
}: ToolbarProps<TData>) => {
  const { t } = useTranslation();
  const controlVariant = hasBorderedControls ? "secondary" : "subtle";
  // Bordered controls line up with the search input and the Filters button;
  // ActionIcon's own default (28px) is shorter than both.
  const controlSize = hasBorderedControls ? "input-sm" : undefined;
  const hideableColumns = meqInstance.getHideableColumns();
  const isAllColumnsVisible = hideableColumns.every(
    (column) => column.isVisible,
  );
  const isNoColumnsVisible = hideableColumns.every(
    (column) => !column.isVisible,
  );

  const handleCycleDensity = () => {
    const nextIndex =
      (DENSITY_ORDER.indexOf(density) + 1) % DENSITY_ORDER.length;
    onDensityChange(DENSITY_ORDER[nextIndex]);
  };

  return (
    <Group justify="space-between" p="sm" wrap="wrap">
      <Group gap="sm">
        {title && (
          // Old-wrapper parity: the old, now-deleted MRT-based wrapper
          // rendered `title` as MUI `<Typography variant="textxl" fontWeight="semiBold">`
          // (Theme/index.ts: textxl = 1.25rem/20px). Mantine's own "xl"
          // fontSizes token is 20px too (Theme/mantine.ts merges Mantine's
          // DEFAULT_THEME, which sets fontSizes.xl via rem(20)) — an exact
          // token match, not a coincidence, so no raw rem fallback needed.
          <Text size="xl" fw={600}>
            {title}
          </Text>
        )}
        {renderToolbarActions?.({ table: meqInstance })}
        {hasExport && !isExportButtonIcon && (
          <ExportCsvButton
            meqInstance={meqInstance}
            isIcon={false}
            fileName={exportFileName}
            onExport={onExportDataToCsv}
            localization={localization}
          />
        )}
      </Group>
      <Group gap="sm">
        {renderToolbarActionsRight?.({ table: meqInstance })}
        {hasGlobalFilter && (
          <GlobalFilterInput
            value={meqInstance.getGlobalFilter()}
            onChange={meqInstance.setGlobalFilter}
            hasBorderedControls={hasBorderedControls}
            localization={localization}
          />
        )}
        {hasColumnHiding && hideableColumns.length > 0 && (
          <Menu shadow="md" width={220} closeOnItemClick={false}>
            <Menu.Target>
              <ActionIcon
                variant={controlVariant}
                size={controlSize}
                color="gray"
                aria-label={
                  localization?.showHideColumns ?? t("table.showHideColumns")
                }
              >
                <SvgIconRaw name="Column" size={20} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() =>
                  meqInstance.toggleAllColumnsVisible(!isAllColumnsVisible)
                }
              >
                <Checkbox
                  size="xs"
                  label={
                    localization?.showHideAllColumns ??
                    t("table.showHideAllColumns")
                  }
                  readOnly
                  tabIndex={-1}
                  style={{ pointerEvents: "none" }}
                  checked={isAllColumnsVisible}
                  indeterminate={!isAllColumnsVisible && !isNoColumnsVisible}
                />
              </Menu.Item>
              <Menu.Divider />
              {/* Cap the column list height so tables with many columns
                  scroll inside the dropdown instead of blowing out the frame
                  (e.g. the nested detail-panel table on Summary of Images). */}
              <ScrollArea.Autosize mah={280} type="scroll">
                {hideableColumns.map((column) => (
                  <Menu.Item
                    key={column.id}
                    onClick={() =>
                      meqInstance.toggleColumnVisibility(column.id)
                    }
                  >
                    <Checkbox
                      size="xs"
                      label={column.header}
                      readOnly
                      tabIndex={-1}
                      style={{ pointerEvents: "none" }}
                      checked={column.isVisible}
                    />
                  </Menu.Item>
                ))}
              </ScrollArea.Autosize>
            </Menu.Dropdown>
          </Menu>
        )}
        {hasDensityToggle && (
          <ActionIcon
            variant={controlVariant}
            size={controlSize}
            color="gray"
            aria-label={localization?.toggleDensity ?? t("table.toggleDensity")}
            onClick={handleCycleDensity}
          >
            <SvgIconRaw name="Density" size={20} />
          </ActionIcon>
        )}
        {hasFullscreenToggle && (
          <ActionIcon
            variant={controlVariant}
            size={controlSize}
            color="gray"
            aria-label={
              localization?.toggleFullscreen ?? t("table.toggleFullscreen")
            }
            onClick={onToggleFullscreen}
          >
            <SvgIconRaw
              name={isFullscreen ? "FullScreenExit" : "FullScreen"}
              size={20}
            />
          </ActionIcon>
        )}
        {hasExport && isExportButtonIcon && (
          <ExportCsvButton
            hasBorderedControls={hasBorderedControls}
            meqInstance={meqInstance}
            isIcon
            fileName={exportFileName}
            onExport={onExportDataToCsv}
            localization={localization}
          />
        )}
        {renderToolbarActionsRightEnd?.({ table: meqInstance })}
      </Group>
    </Group>
  );
};

export default Toolbar;
